import { Countries } from "@/enums/countries.enum";
import { capitalize } from "@/utils/string";

import locationsData from "./locations_generated.json";

interface IAreaData {
  name: string;
  slug: string;
}

interface ILocationData {
  name: string;
  slug: string;
  area?: IAreaData;
}

interface ICountryData {
  country_name: string;
  country_slug: string;
  locations: ILocationData[];
}

type LocationsMap = Record<string, ICountryData>;

const locationsMap = locationsData as LocationsMap;

export interface ILocationMatchResult {
  country: Countries;
  country_slug: string | null;
  location: string;
  location_slug: string | null;
  state?: string;
  state_slug: string | null;
  city: string | null;
  city_slug: string | null;
}

export const generateSlug = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\\-]+/g, "") // Remove all non-word chars (like !, ?, etc.)
    .replace(/\\-\\-+/g, "-");

/**
 * Matches a location string against the enhanced locations JSON file
 * and returns the country, location, and their corresponding slugs.
 *
 * @param locationSlug - The location string from the job (e.g., "London, United Kingdom" or "London, GB")
 * @param countryCode - Optional country code if already known
 * @returns LocationMatchResult with country, location, and slugs
 */
export function getLocationName(
  countryCode: Countries,
  locationSlug: string
): string | null {
  const countryData = locationsMap[countryCode];

  if (!countryData || !countryData.locations) {
    return null;
  }

  // 1. Try an exact match first
  const exactMatch = countryData.locations.find(
    (loc) => loc.slug === locationSlug
  );

  if (exactMatch) {
    return exactMatch.name;
  }

  // 2. Handle comma-separated fallback
  if (locationSlug.includes(",")) {
    // Split and clean the slugs
    const slugs = locationSlug.split(",").map((s) => s.trim());
    // Find the FIRST slug from the list that exists in countryData.locations
    const firstMatch = countryData.locations.find((loc) =>
      slugs.includes(loc.slug)
    );
    return firstMatch ? firstMatch.name : slugs[0] ?? null;
  }

  return capitalize(locationSlug);
}
